import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
// Utility to convert array of objects to CSV string
function arrayToCSV(data, columns) {
    if (!data || !data.length) return '';
    const header = columns.map(col => col.headerName || col.field);
    const rows = data.map(row =>
        columns.map(col => {
            let val = row[col.field];
            if (val === undefined || val === null) return '';
            // Escape quotes and commas
            val = String(val).replace(/"/g, '""');
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                val = '"' + val + '"';
            }
            return val;
        }).join(',')
    );
    return [header.join(','), ...rows].join('\n');
}

function downloadCSV(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const FILTERS_STORAGE_KEY = 'schoolStatusFilters';

const ILG_PAST_WINNER_STATUSES = ['Past Winner', 'Past Winners'];

function isIlgPastWinnerStatus(status) {
    return ILG_PAST_WINNER_STATUSES.includes(status);
}

const columns = [
    { field: 'id', headerName: 'ID', flex: 0.3, minWidth: 56 },
    {
        field: 'schoolName', headerName: 'School Name', flex: 1.125, minWidth: 225, renderCell: (params) => (
            <Link to={`/admin/schools/${params.row.id}`} style={{ textDecoration: 'none' }}>{params.value}</Link>
        )
    },
    { field: 'schoolInformation', headerName: 'School Information', flex: 1, minWidth: 250 },
    { field: 'cigApplication', headerName: 'CIG Application', flex: 1, minWidth: 220 },
    { field: 'ilgApplication', headerName: 'ILG Application', flex: 1, minWidth: 220 },
];

export default function SchoolStatus() {
    const initialFilters = React.useMemo(() => {
        const defaults = {
            filter: 'All',
            cigFilter: 'All',
            ilgFilter: 'All',
            excludeDq: false,
            search: '',
            advancedForm: '',
            advancedStatus: '',
        };

        if (typeof window === 'undefined') return defaults;

        try {
            const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
            if (!raw) return defaults;
            const parsed = JSON.parse(raw);
            return {
                ...defaults,
                ...(parsed && typeof parsed === 'object' ? parsed : {}),
            };
        } catch (e) {
            return defaults;
        }
    }, []);

    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState(initialFilters.filter);
    const [cigFilter, setCigFilter] = useState(initialFilters.cigFilter);
    const [ilgFilter, setIlgFilter] = useState(initialFilters.ilgFilter);
    const [excludeDq, setExcludeDq] = useState(Boolean(initialFilters.excludeDq));
    const [search, setSearch] = useState(initialFilters.search || '');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/admin/api/schools/status');
            setRows(res.data || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load application status');
        } finally {
            setLoading(false);
        }
    };

    // debounce the search input for snappy UX
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const baseRows = React.useMemo(() => {
        if (!excludeDq) return rows;
        return rows.filter(r => !r.dq);
    }, [rows, excludeDq]);

    // derive filteredRows from search + chips filters
    const filteredRows = React.useMemo(() => {
        const q = debouncedSearch && debouncedSearch.toLowerCase();
        return baseRows.filter(r => {
            // search by schoolName or id (case-insensitive substring)
            let okSearch = true;
            if (q) okSearch = (r.schoolName || '').toLowerCase().includes(q) || (String(r.id || '')).includes(q);

            // filter by SchoolInfo
            let okSchool = true;
            if (filter && filter !== 'All') {
                if (filter === 'Other') okSchool = (r.schoolInformation !== 'Completed' && r.schoolInformation !== 'Draft - In Progress' && r.schoolInformation !== 'Not Started');
                else okSchool = (r.schoolInformation === filter);
            }
            // filter by CIG
            let okCig = true;
            if (cigFilter && cigFilter !== 'All') {
                if (cigFilter === 'Other') okCig = (r.cigApplication !== 'Completed' && r.cigApplication !== 'Draft - In Progress' && r.cigApplication !== 'Not Started');
                else okCig = (r.cigApplication === cigFilter);
            }
            // filter by ILG as well
            let okIlg = true;
            if (ilgFilter && ilgFilter !== 'All') {
                const ilgValue = r.ilgApplication;
                if (ilgFilter === 'Other') {
                    okIlg = (
                        ilgValue !== 'Completed' &&
                        ilgValue !== 'Draft - In Progress' &&
                        ilgValue !== 'Not Started' &&
                        !isIlgPastWinnerStatus(ilgValue)
                    );
                }
                else if (ilgFilter === 'Past Winner') okIlg = isIlgPastWinnerStatus(ilgValue);
                else okIlg = (ilgValue === ilgFilter);
            }

            return okSearch && okSchool && okCig && okIlg;
        });
    }, [baseRows, filter, cigFilter, ilgFilter, debouncedSearch]);

    const counts = React.useMemo(() => {
        const c = { Completed: 0, 'Draft - In Progress': 0, 'Not Started': 0, Other: 0 };
        filteredRows.forEach(r => {
            const s = r.schoolInformation;
            if (s === 'Completed') c.Completed += 1;
            else if (s === 'Draft - In Progress') c['Draft - In Progress'] += 1;
            else if (s === 'Not Started') c['Not Started'] += 1;
            else c.Other += 1;
        });
        return c;
    }, [filteredRows]);

    const cigCounts = React.useMemo(() => {
        const c = { Completed: 0, 'Draft - In Progress': 0, 'Not Started': 0, Other: 0 };
        filteredRows.forEach(r => {
            const s = r.cigApplication;
            if (s === 'Completed') c.Completed += 1;
            else if (s === 'Draft - In Progress') c['Draft - In Progress'] += 1;
            else if (s === 'Not Started') c['Not Started'] += 1;
            else c.Other += 1;
        });
        return c;
    }, [filteredRows]);

    const ilgCounts = React.useMemo(() => {
        const c = { Completed: 0, 'Draft - In Progress': 0, 'Not Started': 0, 'Past Winner': 0, Other: 0 };
        filteredRows.forEach(r => {
            const s = r.ilgApplication;
            if (s === 'Completed') c.Completed += 1;
            else if (s === 'Draft - In Progress') c['Draft - In Progress'] += 1;
            else if (s === 'Not Started') c['Not Started'] += 1;
            else if (isIlgPastWinnerStatus(s)) c['Past Winner'] += 1;
            else c.Other += 1;
        });
        return c;
    }, [filteredRows]);

    // chip color tokens
    const chipColors = {
        completedBg: '#E6F9E6', // light green
        completedText: '#1B5E20',
        draftBg: '#FFF4E6', // light orange
        draftText: '#E65100',
        notStartedBg: '#FFECEC', // light red/pink
        notStartedText: '#B71C1C',
        outlinedBorder: '#E0E0E0'
    };

    // advanced selector state: choose a form column and a status
    const [advancedForm, setAdvancedForm] = useState(initialFilters.advancedForm || ''); // 'schoolInformation' | 'cigApplication' | 'ilgApplication'
    const [advancedStatus, setAdvancedStatus] = useState(initialFilters.advancedStatus || '');

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
                filter,
                cigFilter,
                ilgFilter,
                excludeDq,
                search,
                advancedForm,
                advancedStatus,
            }));
        } catch (e) {
            // Ignore storage write failures (e.g. browser privacy mode restrictions).
        }
    }, [filter, cigFilter, ilgFilter, excludeDq, search, advancedForm, advancedStatus]);

    const handleReset = () => {
        setFilter('All');
        setCigFilter('All');
        setIlgFilter('All');
        setSearch('');
        setAdvancedForm('');
        setAdvancedStatus('');
        setExcludeDq(false);

        if (typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(FILTERS_STORAGE_KEY);
            } catch (e) {
                // Ignore storage write failures.
            }
        }
    };

    const advancedFilteredRows = React.useMemo(() => {
        if (!advancedForm || !advancedStatus) return filteredRows;
        return filteredRows.filter(r => {
            const val = r[advancedForm];
            if (advancedStatus === 'Other') return (val !== 'Completed' && val !== 'Draft - In Progress' && val !== 'Not Started');
            return val === advancedStatus;
        });
    }, [filteredRows, advancedForm, advancedStatus]);

    const displayRows = (advancedForm && advancedStatus) ? advancedFilteredRows : filteredRows;

    const exportRows = React.useMemo(() => {
        return displayRows.map((row) => ({
            ...row,
            dqStatus: row.dq ? 'DQ\'d' : 'Not DQ\'d',
        }));
    }, [displayRows]);

    const exportColumns = React.useMemo(() => ([
        { field: 'id', headerName: 'ID' },
        { field: 'schoolName', headerName: 'School Name' },
        { field: 'schoolInformation', headerName: 'School Information' },
        { field: 'cigApplication', headerName: 'CIG Application' },
        { field: 'ilgApplication', headerName: 'ILG Application' },
        { field: 'dqStatus', headerName: 'DQ Status' },
    ]), []);

    const minimalColumns = React.useMemo(() => [
        { field: 'id', headerName: 'ID', flex: 0.3, minWidth: 56 },
        {
            field: 'schoolName', headerName: 'School Name', flex: 1.125, minWidth: 225, renderCell: (params) => (
                <Link to={`/admin/schools/${params.row.id}`} style={{ textDecoration: 'none' }}>{params.value}</Link>
            )
        },
    ], []);

    // build columns with chip-enabled headers so chips appear inside grid headers
    const columnsMemo = React.useMemo(() => {
        return [
            { field: 'id', headerName: 'ID', flex: 0.3, minWidth: 56 },
            {
                field: 'schoolName', headerName: 'School Name', flex: 1.125, minWidth: 225, renderCell: (params) => (
                    <Link to={`/schools/${params.row.id}`} style={{ textDecoration: 'none' }}>{params.value}</Link>
                )
            },
            {
                field: 'schoolInformation',
                headerName: 'School Information',
                flex: 1,
                minWidth: 250,
                renderHeader: () => (
                    <Box sx={{ pb: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textDecoration: 'underline' }}>School Information</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.25, py: 0.25 }}>
                            <Chip
                                size="small"
                                label={`Completed: ${counts.Completed}`}
                                variant={filter === 'Completed' ? 'filled' : 'outlined'}
                                onClick={() => setFilter(prev => prev === 'Completed' ? 'All' : 'Completed')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.completedBg,
                                    color: chipColors.completedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Draft: ${counts['Draft - In Progress']}`}
                                variant={filter === 'Draft - In Progress' ? 'filled' : 'outlined'}
                                onClick={() => setFilter(prev => prev === 'Draft - In Progress' ? 'All' : 'Draft - In Progress')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.draftBg,
                                    color: chipColors.draftText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Not Started: ${counts['Not Started']}`}
                                variant={filter === 'Not Started' ? 'filled' : 'outlined'}
                                onClick={() => setFilter(prev => prev === 'Not Started' ? 'All' : 'Not Started')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.notStartedBg,
                                    color: chipColors.notStartedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                        </Box>
                    </Box>
                )
            },
            {
                field: 'cigApplication',
                headerName: 'CIG Application',
                flex: 1,
                minWidth: 220,
                renderHeader: () => (
                    <Box sx={{ pb: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textDecoration: 'underline' }}>CIG Application</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.25, py: 0.25 }}>
                            <Chip
                                size="small"
                                label={`Completed: ${cigCounts.Completed}`}
                                variant={cigFilter === 'Completed' ? 'filled' : 'outlined'}
                                onClick={() => setCigFilter(prev => prev === 'Completed' ? 'All' : 'Completed')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.completedBg,
                                    color: chipColors.completedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Draft: ${cigCounts['Draft - In Progress']}`}
                                variant={cigFilter === 'Draft - In Progress' ? 'filled' : 'outlined'}
                                onClick={() => setCigFilter(prev => prev === 'Draft - In Progress' ? 'All' : 'Draft - In Progress')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.draftBg,
                                    color: chipColors.draftText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Not Started: ${cigCounts['Not Started']}`}
                                variant={cigFilter === 'Not Started' ? 'filled' : 'outlined'}
                                onClick={() => setCigFilter(prev => prev === 'Not Started' ? 'All' : 'Not Started')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.notStartedBg,
                                    color: chipColors.notStartedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                        </Box>
                    </Box>
                )
            },
            {
                field: 'ilgApplication',
                headerName: 'ILG Application',
                flex: 1,
                minWidth: 220,
                renderHeader: () => (
                    <Box sx={{ pb: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textDecoration: 'underline' }}>ILG Application</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25, py: 0.25 }}>
                            <Chip
                                size="small"
                                label={`Completed: ${ilgCounts.Completed}`}
                                variant={ilgFilter === 'Completed' ? 'filled' : 'outlined'}
                                onClick={() => setIlgFilter(prev => prev === 'Completed' ? 'All' : 'Completed')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.completedBg,
                                    color: chipColors.completedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Draft: ${ilgCounts['Draft - In Progress']}`}
                                variant={ilgFilter === 'Draft - In Progress' ? 'filled' : 'outlined'}
                                onClick={() => setIlgFilter(prev => prev === 'Draft - In Progress' ? 'All' : 'Draft - In Progress')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.draftBg,
                                    color: chipColors.draftText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Not Started: ${ilgCounts['Not Started']}`}
                                variant={ilgFilter === 'Not Started' ? 'filled' : 'outlined'}
                                onClick={() => setIlgFilter(prev => prev === 'Not Started' ? 'All' : 'Not Started')}
                                clickable
                                sx={{
                                    backgroundColor: chipColors.notStartedBg,
                                    color: chipColors.notStartedText,
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`Past Winner: ${ilgCounts['Past Winner']}`}
                                variant={ilgFilter === 'Past Winner' ? 'filled' : 'outlined'}
                                onClick={() => setIlgFilter(prev => prev === 'Past Winner' ? 'All' : 'Past Winner')}
                                clickable
                                sx={{
                                    borderColor: chipColors.outlinedBorder,
                                    py: 0.25,
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        ];
    }, [counts, cigCounts, ilgCounts, filter, cigFilter, ilgFilter]);

    const displayColumns = (advancedForm && advancedStatus) ? minimalColumns : columnsMemo;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h4">Application Status</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                // Export always includes DQ status while still respecting active filters.
                                const csv = arrayToCSV(exportRows, exportColumns);
                                downloadCSV(csv, 'school-status-export.csv');
                            }}
                        >
                            Export CSV
                        </Button>
                    </Box>
                </Box>

                {/* removed top-left chip groups; chips are now rendered inside grid column headers */}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* search on top, advanced selectors below */}
            <Box sx={{ width: '100%', mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Search school name or ID"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearch('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{ width: 320 }}
                            />
                            <FormControlLabel
                                control={<Switch checked={excludeDq} onChange={(e) => setExcludeDq(e.target.checked)} />}
                                label="Exclude Disqualified Schools"
                                sx={{ ml: 1, mr: 1 }}
                            />
                            <Button variant="outlined" onClick={handleReset}>Reset</Button>
                        </Box>

                        <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="advanced-form-label">Form</InputLabel>
                                <Select
                                    labelId="advanced-form-label"
                                    value={advancedForm}
                                    label="Form"
                                    onChange={(e) => { setAdvancedForm(e.target.value); setAdvancedStatus(''); }}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="schoolInformation">School Info</MenuItem>
                                    <MenuItem value="cigApplication">CIG Application</MenuItem>
                                    <MenuItem value="ilgApplication">ILG Application</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="advanced-status-label">Status</InputLabel>
                                <Select
                                    labelId="advanced-status-label"
                                    value={advancedStatus}
                                    label="Status"
                                    onChange={(e) => setAdvancedStatus(e.target.value)}
                                    disabled={!advancedForm}
                                >
                                    <MenuItem value="">Any</MenuItem>
                                    <MenuItem value="Completed">Completed</MenuItem>
                                    <MenuItem value="Draft - In Progress">Draft - In Progress</MenuItem>
                                    <MenuItem value="Not Started">Not Started</MenuItem>
                                    <MenuItem value="Past Winner">Past Winner</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                            </FormControl>

                            <Button variant="outlined" onClick={() => { setAdvancedForm(''); setAdvancedStatus(''); }}>Clear</Button>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={displayRows}
                    columns={displayColumns}
                    headerHeight={104}
                    rowHeight={48}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        // Force the header container to a fixed taller height (px)
                        '& .MuiDataGrid-columnHeaders': {
                            boxSizing: 'border-box',
                            minHeight: 104,
                            maxHeight: 104,
                            height: 104,
                        },
                        // Make each header cell fill the header container and align content to the top
                        '& .MuiDataGrid-columnHeader': {
                            height: '104px !important',
                            minHeight: '104px !important',
                            maxHeight: '104px !important',
                            alignItems: 'flex-start',
                            overflow: 'visible',
                            boxSizing: 'border-box',
                            paddingTop: '6px',
                        },
                        // Ensure the title container can wrap and sit above the chips
                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            display: 'block',
                            paddingTop: '6px',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            display: 'block',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            fontWeight: 700,
                            textDecoration: 'underline',
                            marginTop: 0,
                        },
                        // Ensure chips inside headers do not inherit bold/underline
                        '& .MuiDataGrid-columnHeader .MuiChip-root, & .MuiDataGrid-columnHeader .MuiChip-label': {
                            fontWeight: 400,
                            textDecoration: 'none',
                        },
                        // Force data rows to a consistent smaller height so header looks taller
                        '& .MuiDataGrid-row': {
                            minHeight: 48,
                            maxHeight: 48,
                            height: 48,
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
